import { IGrantEntity } from "@odata/GeneratedEntityTypes";
import { GrantStatusCode } from "@odata/GeneratedEnums";
import { clearBPIfNotSet, updateBPRelation } from "@pages/businessPartner/BusinessPartner.utils";
import { clearStoredRanged, setMatchingNumberRange } from "@pages/numberRange/NumberRange.utils";
import { cloneDeep } from "lodash";

import { withPermissionContext } from "../../contexts/permissionContext/withPermissionContext";
import { FormViewForExtend, IFormViewProps } from "../../views/formView/FormView";
import React, { ReactElement } from "react";
import Dialog from "@components/dialog/Dialog";
import DialogPage from "@pages/DialogPage";
import { PageViewMode } from "../../enums";
import { StorageModel } from "../../model/StorageModel";
import GrantPairTableView from "@pages/grant/GrantPairTableView";
import { getDefinitions } from "@pages/grant/GrantPairDef";
import { IGrantCustomData } from "@pages/grant/Grant.utils";
import { FormStorage } from "../../views/formView/FormStorage";
import { ActionType, ISmartFastEntriesActionEvent } from "@components/smart/smartFastEntryList";
import { ISmartFieldChange } from "@components/smart/smartField/SmartField";
import { Button, ButtonGroup } from "@components/button";
import SmartFormDeleteButton from "@components/smart/smartFormDeleteButton/SmartFormDeleteButton";

interface IProps extends IFormViewProps<IGrantEntity> {
}


class GrantFormView extends FormViewForExtend<IGrantEntity, IProps> {

    get storage() {
        return this.props.storage as FormStorage<IGrantEntity, IGrantCustomData>;
    }

    async onAfterLoad(): Promise<void> {
        const storage = this.props.storage;

        if (storage.data.bindingContext.isNew()) {
            await setMatchingNumberRange(storage);
        }

        return super.onAfterLoad();
    }

    handleCloseClick = () => {
        this.props.storage.data.entity.IsClosed = true;
        this.save({
            successSubtitle: this.props.storage.t("Grants:Form.SuccessfulClosed")
        });
    }

    handleReopenedClick = () => {
        this.props.storage.data.entity.IsClosed = false;
        this.save({
            successSubtitle: this.props.storage.t("Grants:Form.SuccessfulReopened")
        });
    }

    handleSaveClick = (): void => {
        this.save();
    };

    renderButtons(): ReactElement {
        const storage = this.props.storage;
        const isClosed = storage.data.entity.Balance?.Status?.Code === GrantStatusCode.Closed;
        const isNew = storage.data.bindingContext.isNew();

        return <ButtonGroup wrap={"wrap"}>
            <Button hotspotId={"formCancel"}
                    isDisabled={storage.isDisabled || this.shouldDisableCancelButton()}
                    onClick={this.handleFormCancel}
                    isTransparent>{storage.t("Common:General.Cancel")}</Button>

            {!isNew &&
                <>
                {this.isDeletable &&
                    <SmartFormDeleteButton storage={storage}
                                           isDisabled={isClosed || storage.isDisabled}
                                           onClick={this.handleDelete}/>
                }

                    <Button hotspotId={"close"}
                            isDisabled={storage.isDisabled}
                            isTransparent
                            onClick={isClosed ? this.handleReopenedClick : this.handleCloseClick}>
                        {isClosed ? storage.t("Grants:Form.Reopen") : storage.t("Grants:Form.Close")}</Button>
                </>
            }
            <Button hotspotId={"formSave"}
                    onClick={this.handleSaveClick}
                    isDisabled={this.shouldDisableSaveButton()}>
                {storage.t("Common:General.Save")}
            </Button>
        </ButtonGroup>
    }


    onBeforeSave(): IGrantEntity {
        const { storage } = this.props;
        const data = cloneDeep(storage.data.entity);

        delete data.Balance;

        clearBPIfNotSet(data);
        updateBPRelation(data);

        return data;
    }

    handleClosePairing = () => {
        this.storage.setCustomData({ pairedDialogOpened: false });
        this.forceUpdate();
    }

    handleLineItemsAction = (args: ISmartFastEntriesActionEvent): void => {
        if (args.actionType === ActionType.Custom) {
            this.storage.setCustomData({ pairedDialogOpened: true });
        } else {
            this.props.storage.handleLineItemsAction(args);
        }
        this.props.storage.refresh();
    };

    handleChange = (e: ISmartFieldChange) => {
        super.handleChange(e);

        this.refreshAddFromUnsortedButton(e);
        this.props.storage.refreshFields();
    }

    refreshAddFromUnsortedButton(e: ISmartFieldChange) {
        if (this.props.storage.data.bindingContext.isNew() && ["ShortName", "Name"].includes(e.bindingContext.getPath())) {
            this.props.storage.refreshGroupByKey("Pair");
        }
    }


    renderClearingDialog = (): ReactElement => {
        if (!this.storage.getCustomData().pairedDialogOpened) {
            return null;
        }

        return <Dialog
            isEditableWindow
            onConfirm={null}
            onClose={this.handleClosePairing}>
            <DialogPage
                pageViewMode={PageViewMode.FormReadOnly}
                rootStorage={this.storage as StorageModel}
                tableView={GrantPairTableView}
                getDef={getDefinitions}/>
        </Dialog>;
    };

    renderCustomDialogs = () => {
        return this.renderClearingDialog();
    }

    onAfterSave(isNew: boolean, preventTableViewRefresh: boolean) {
        super.onAfterSave(isNew, preventTableViewRefresh);
        clearStoredRanged(this.props.storage);
    }
}

export default withPermissionContext(GrantFormView);
